import React, { useCallback, useState } from "react";

interface WalletConnectProps {
  account: string | null;
  isConnecting: boolean;
  onConnect: () => void;
}

const WalletConnect: React.FC<WalletConnectProps> = ({
  account,
  isConnecting,
  onConnect,
}) => {
  const [copied, setCopied] = useState(false);

  // Safe copy that works without HTTPS (falls back to execCommand)
  const copyAddress = useCallback(async () => {
    if (!account) return;
    try {
      if (
        typeof window !== "undefined" &&
        window.isSecureContext &&
        navigator?.clipboard?.writeText
      ) {
        await navigator.clipboard.writeText(account);
      } else {
        const ta = document.createElement("textarea");
        ta.value = account;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error("Copy failed:", e);
    }
  }, [account]);

  if (account) {
    const addr = account;
    const short = `${addr.slice(0, 6)}…${addr.slice(-4)}`;

    return (
      <div className="relative group">
        {/* Ambient glow effect */}
        <div className="absolute -inset-0.5 rounded-xl bg-gradient-to-r from-emerald-500/20 via-cyan-500/20 to-blue-500/20 
                        blur-lg opacity-40 group-hover:opacity-60 transition-all duration-300" />

        {/* Main connected wallet bar - thin and sleek */}
        <div className="relative flex items-center gap-3 px-4 py-2.5 rounded-xl 
                        bg-gradient-to-r from-gray-900/95 to-gray-800/95 backdrop-blur-xl 
                        border border-gray-700/50 hover:border-emerald-500/30
                        shadow-lg hover:shadow-emerald-500/10 transition-all duration-300">
          
          {/* Status indicator dot */}
          <div className="relative flex items-center gap-2">
            <div className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-400/50" />
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
              Connected
            </span>
          </div>

          {/* Address display */}
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 
                            flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            
            <span className="font-mono text-sm text-white/90 truncate tracking-wide">
              {short}
            </span>
          </div>

          {/* Copy button - compact */}
          <button
            onClick={copyAddress}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg 
                       bg-gray-800/60 hover:bg-gray-700/80 border border-gray-600/50 
                       hover:border-gray-500/70 text-gray-300 hover:text-white 
                       text-xs font-medium transition-all duration-200 
                       focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            title="Copy wallet address"
          >
            {copied ? (
              <>
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-emerald-400">Copied</span>
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2}>
                  <rect x="9" y="9" width="10" height="10" rx="2" />
                  <path d="M7 15H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1" />
                </svg>
                <span>Copy</span>
              </>
            )}
          </button>

          {/* Network indicator */}
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md 
                          bg-amber-500/20 border border-amber-500/30">
            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
            <span className="text-xs text-amber-300 font-medium">BSC</span>
          </div>
        </div>
      </div>
    );
  }

  // Connect button - keeping the original awesome design
  return (
    <button
      onClick={onConnect}
      disabled={isConnecting}
      className="group relative w-full rounded-xl px-4 py-3 font-semibold text-white transition-all 
                 bg-gradient-to-r from-orange-300 to-orange-400 
                 shadow-[0_12px_30px_-10px_rgba(16,185,129,0.65)] 
                 hover:from-orange-200 hover:to-orange-300 
                 focus:outline-none focus:ring-4 focus:ring-emerald-400/40 
                 disabled:opacity-60 disabled:cursor-not-allowed"
      aria-busy={isConnecting}
      aria-live="polite"
    >
      <span className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-white/20" />
      <span className="flex items-center justify-center gap-2">
        {isConnecting ? (
          <span className="h-4 w-4 rounded-full border-2 border-white/50 border-t-transparent animate-spin" aria-hidden="true" />
        ) : (
          <img
            src="https://images.ctfassets.net/clixtyxoaeas/1ezuBGezqfIeifWdVtwU4c/d970d4cdf13b163efddddd5709164d2e/MetaMask-icon-Fox.svg"
            alt="MetaMask"
            className="h-4 w-4 shrink-0"
            draggable={false}
          />
        )}
        <span>{isConnecting ? "Connecting..." : "Connect MetaMask"}</span>
      </span>
    </button>
  );
};

export default WalletConnect;